import React, { useEffect, useState } from 'react';
import { Landmark, TrendingUp, TrendingDown, Minus, ChevronDown, GraduationCap, Info } from 'lucide-react';
import { fetchAuctionWatch } from '../services/apiClient';
import { formatDate } from '../utils/dateUtils';

// Color treatment per grade band (consistent with the app's signal palette).
function gradeTone(score) {
  if (score >= 86) return { text: 'text-green-300', bg: 'bg-green-400/10', border: 'border-green-400/30' };
  if (score >= 76) return { text: 'text-emerald-200', bg: 'bg-emerald-400/10', border: 'border-emerald-400/25' };
  if (score >= 64) return { text: 'text-yellow-200', bg: 'bg-yellow-400/10', border: 'border-yellow-400/30' };
  return { text: 'text-red-200', bg: 'bg-red-400/10', border: 'border-red-400/30' };
}

function actionTone(action) {
  switch (action) {
    case 'Stay Invested': return 'text-green-200 border-green-400/30 bg-green-400/10';
    case 'Keep an Eye on This': return 'text-yellow-200 border-yellow-400/30 bg-yellow-400/10';
    case 'Tighten Stops': return 'text-orange-200 border-orange-400/30 bg-orange-400/10';
    default: return 'text-red-200 border-red-400/30 bg-red-400/10'; // Consider Raising Cash
  }
}

function TrendBadge({ trend }) {
  if (trend === 'improving') return <span className="inline-flex items-center gap-1 text-xs text-green-300"><TrendingUp size={13} /> Improving</span>;
  if (trend === 'deteriorating') return <span className="inline-flex items-center gap-1 text-xs text-red-300"><TrendingDown size={13} /> Deteriorating</span>;
  return <span className="inline-flex items-center gap-1 text-xs text-mentor-muted"><Minus size={13} /> Steady</span>;
}

export default function AuctionWatchCard({ compact = false, data: providedData = null, managed = false }) {
  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(!managed);
  const [learnOpen, setLearnOpen] = useState(false);

  useEffect(() => {
    if (managed) return undefined; // parent owns the data via the `data` prop
    let active = true;
    fetchAuctionWatch()
      .then((res) => { if (active) setFetched(res); })
      .catch(() => { if (active) setFetched({ available: false }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [managed]);

  const data = managed ? providedData : fetched;
  const isLoading = managed ? !data : loading;

  if (isLoading) {
    return <p className="text-sm text-mentor-muted">Loading recent Treasury auctions…</p>;
  }

  if (!data?.available || !data?.auctions?.length) {
    return (
      <div className="rounded-xl border border-mentor-border/50 bg-white/[0.02] p-4 text-sm text-mentor-muted">
        No live auction grades yet — this fills in automatically after the next auctions settle. Any &ldquo;scoreboard&rdquo; shown elsewhere is labeled sample data for learning.
      </div>
    );
  }

  const { auctions, climate } = data;
  const climateTone = gradeTone(climate.score);
  const rows = compact ? auctions.slice(0, 3) : auctions;

  return (
    <div className="space-y-4">
      {/* Climate header — the rolling momentum read */}
      <div className={`rounded-2xl border ${climateTone.border} ${climateTone.bg} p-4`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`flex h-12 w-12 items-center justify-center rounded-xl border ${climateTone.border} ${climateTone.bg}`}>
              <span className={`font-display font-black text-lg ${climateTone.text}`}>{climate.grade}</span>
            </div>
            <div>
              <p className="font-display font-bold text-mentor-text text-sm">Auction climate · {climate.label}</p>
              <p className="text-xs text-mentor-muted">Average of the last {Math.min(climate.count, 5)} auctions · {climate.score}/100</p>
            </div>
          </div>
          <TrendBadge trend={climate.trend} />
        </div>
        <p className="text-xs text-mentor-muted mt-3 leading-relaxed">
          This is how eagerly the world has been lending to the U.S. lately. A run of strong auctions is calming; a run of weak ones can push interest rates up. <span className="text-mentor-text/70">Context only — it doesn’t change your Mission Control signal.</span>
        </p>
      </div>

      {/* Recent auctions */}
      <div className="space-y-3">
        {rows.map((a, i) => {
          const tone = gradeTone(a.score);
          return (
            <div key={`${a.date}-${a.term}-${i}`} className="rounded-xl border border-mentor-border/50 bg-white/[0.02] p-4">
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Landmark size={16} className="text-mentor-accent" />
                  <span className="font-display font-bold text-mentor-text text-sm">{a.term}</span>
                  <span className="text-xs text-mentor-muted">· {formatDate(a.date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  {a.yield != null && <span className="text-xs text-mentor-muted">Yield <span className="text-mentor-text font-semibold">{Number(a.yield).toFixed(3)}%</span></span>}
                  <span className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 font-display font-bold text-sm ${tone.border} ${tone.bg} ${tone.text}`}>{a.grade}</span>
                </div>
              </div>

              <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
                <span className={`font-semibold ${tone.text}`}>{a.demand}</span>
                <span className="text-mentor-muted">· {a.score}/100</span>
                <span className="text-mentor-muted">· bid-to-cover {a.bidToCover?.toFixed(2)}</span>
                {a.indirectSharePct != null && <span className="text-mentor-muted">· foreign/indirect {a.indirectSharePct}%</span>}
                <span className={`rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${a.confidence === 'high' ? 'border-green-400/30 text-green-300' : 'border-mentor-border/60 text-mentor-muted'}`}>
                  {a.confidence === 'high' ? 'High confidence' : 'Estimated'}
                </span>
              </div>

              <p className="text-sm text-mentor-text/90 mt-3 leading-relaxed">{a.meaning}</p>
              <p className="text-xs text-mentor-muted mt-1.5 leading-relaxed"><span className="font-semibold text-mentor-text/70">What to watch:</span> {a.watch}</p>

              <div className="mt-3">
                <span className={`inline-flex items-center rounded-lg border px-2.5 py-1 text-xs font-display font-bold ${actionTone(a.action)}`}>{a.action}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Learn drawer */}
      <div className="rounded-xl border border-mentor-border/50 bg-white/[0.02]">
        <button type="button" onClick={() => setLearnOpen((v) => !v)} className="w-full flex items-center justify-between p-3 text-left">
          <span className="inline-flex items-center gap-2 text-sm font-display font-bold text-mentor-text"><GraduationCap size={16} className="text-mentor-accent" /> What do these numbers mean?</span>
          <ChevronDown size={16} className={`text-mentor-muted transition-transform ${learnOpen ? 'rotate-180' : ''}`} />
        </button>
        {learnOpen && (
          <div className="px-4 pb-4 space-y-3 text-sm text-mentor-muted leading-relaxed border-t border-mentor-border/40 pt-3">
            <p><span className="font-semibold text-mentor-text">A Treasury auction</span> is the U.S. government selling IOUs (notes and bonds) to big buyers — banks, foreign governments, pension funds. How eagerly they buy is a real-money vote on confidence in the U.S. and where interest rates are headed.</p>
            <p><span className="font-semibold text-mentor-text">Bid-to-cover</span> is how many dollars of bids showed up for each dollar offered. Higher means a hungrier crowd (strong demand); lower means a lukewarm one. It’s the headline number behind the grade.</p>
            <p><span className="font-semibold text-mentor-text">Foreign / indirect share</span> is roughly how much went to overseas central banks and big institutions. A higher share signals broad, healthy demand. When it’s low, the dealers who are forced to mop up leftovers took more — a softer sign.</p>
            <p><span className="font-semibold text-mentor-text">The grade & action</span> blend these into one plain read. A single soft auction says “keep an eye on this”; a string of weak ones is the bond market voting cautiously, which can lift rates and pressure stocks — so the action steps up to “tighten stops” or “raise cash.”</p>
            <p className="text-xs text-mentor-muted/80">Educational market-awareness only, not personal financial advice.</p>
          </div>
        )}
      </div>

      {compact && auctions.length > rows.length && (
        <p className="text-xs text-mentor-muted inline-flex items-center gap-1"><Info size={12} /> See the Vigilantes tab for the full auction history.</p>
      )}
    </div>
  );
}
